/* =====================================================================
   juizs-ingredient-modal.js — OD-001
   ---------------------------------------------------------------------
   Leest de JSON-eilanden uit `snippets/juizs-ingredient-modal.liquid`
   (één per variant met dagschema) en vult de modal.

   Hergebruikt `window.JuizsOverlay` uit assets/juizs-cart.js: dezelfde
   focus-trap, Esc-afhandeling en scroll-lock als de winkelwagen-lade.

   Triggers, waar dan ook in de pagina:
     [data-juizs-juice="<handle>"]          → opent dat sapje
     [data-juizs-juice-variant="<id>"]      → optioneel: welk dagschema

   Variantwissel volgt `juizs:variant-change` (dispatch uit de buy-box).

   Metaveld-contract (Terry 29-07):
     juizs.ingredients → [["gembersap", 25], ["appelsap", 65]]
                         getal = percentage; een string wordt letterlijk
                         overgenomen (bv. "spoortje").
     juizs.nutrition   → [["Energie", "110 kcal"], ["Vezels", "1,2 g"]]
     juizs.allergens   → vrije tekst
   Voedingswaarden, uitgebreid (golf 7e, Terry "B2 ja" 14-10):
     ["Vitamine C", "133,3 mg", "16,7% RI"]  2e cel met een cijfer = de
                         hoeveelheid (nowrap); 3e cel en verder = kleine
                         regel eronder (RI), mag afbreken.
     ["Per 100 ml:"]     één cel, of alleen tekst zonder cijfer in de 2e
                         cel = notitie over de volle breedte.
     ["Hoog cafeïnegehalte (27 mg per 100 ml)"], ["Niet aanbevolen voor …"],
     ["De aanbevolen dagelijkse hoeveelheid niet overschrijden. …"]
                         notitie die zo begint (WARN_PREFIXES) = waarschuwing:
                         icoon + vet "Let op:" + tekst op een eigen vlak.
   Leeg → het sapje blijft zichtbaar, met een wachttekst in plaats van
   lege lijsten.

   KLEUR (golf 7e, 14-10): de snippet levert `color`/`colorDeep` als kale
   #rrggbb of leeg. Leeg (of iets anders) → TYPE_COLOR hieronder, de enige
   plek met terugvalkleuren. Bolletje en flesje gebruiken hetzelfde paar.
   Onbekend type (bijv. Juizs Thee met type "sapkuur") → groep "Extra"
   achteraan; niets verdwijnt uit de index.
   NIET GEKOPPELD (golf 7e): `linked: false` = de dagschema-handle is geen
   product in de sappenset. Alleen in de theme-editor staat er dan een
   notitie ([data-juizs-imod-editor] bestaat alleen bij request.design_mode).

   PORTAL (golf 7c, 13-10): de modal verhuist bij het starten naar <body>.
   Binnen een sectie kan een mask, clip-path, transform of filter hem
   afknippen en in een eigen stapelcontext opsluiten (de golfrand van de
   detailsectie deed dat: de header lag eroverheen). De data-eilanden en
   de triggers blijven in de sectie staan.
   Theme-editor: bij `shopify:section:unload` van de sectie waar hij
   vandaan kwam, gaat de verhuisde modal weer weg (met zijn listeners);
   `shopify:section:load` start opnieuw. Zo blijft er nooit een tweede of
   verweesde modal in <body> achter.
   ===================================================================== */
(function () {
  'use strict';

  // Eén keer per pagina. Voert de editor dit script bij het herladen van de
  // sectie opnieuw uit, dan regelen de sectie-events hieronder het al.
  // Niet `window.JuizsIngredientModal`: dat is al het element met dat id.
  if (window.JuizsImodal) return;

  var active = null;

  function init() {
    var root =
      document.querySelector('[data-juizs-imodal]:not([data-juizs-portal])') ||
      document.querySelector('[data-juizs-imodal]');
    if (!root || typeof window.JuizsOverlay !== 'function') return null;

    var listeners = [];
    function on(target, type, fn) {
      target.addEventListener(type, fn);
      listeners.push([target, type, fn]);
    }

    var TYPE_ORDER = ['juice', 'smoothie', 'shot', 'veggie', 'extra'];
    var TYPE_LABEL = { juice: 'Juice', smoothie: 'Smoothie', shot: 'Shot', veggie: 'Veggie', extra: 'Extra' };

    // Terugvalkleur per type [licht, diep], alleen als het product zelf geen
    // geldige kleur heeft. Geen nieuwe merkkleuren (Prism-GATE golf 7e):
    // juice    = tokens mint → mint-darker (de oude vaste standaard)
    // smoothie = concept-fles cacao-banana-almond (pdp-sapkuur r2284+)
    // shot     = concept-fles shot-turmeric (≈ --juizs-star)
    // veggie   = concept-fles apple-celery-lemon
    // extra    = tokens peach → coral
    var TYPE_COLOR = {
      juice: ['#8fdfb3', '#3da381'],
      smoothie: ['#8b5a2b', '#4a2c0e'],
      shot: ['#f2b93d', '#c48a15'],
      veggie: ['#a8c4a2', '#6e9469'],
      extra: ['#f5a09a', '#e15b5e'],
    };
    var HEX = /^#[0-9a-f]{6}$/i;

    // Voedingsnotitie die een waarschuwing is (etikettekst): begint met een
    // van deze prefixen, hoofdletterongevoelig; "caffeïne" en "cafeine" tellen
    // als "cafeïne". Bewust smal: "Hoog in vezels" is een claim, geen waarschuwing.
    var WARN_PREFIXES = ['hoog cafeïnegehalte', 'niet aanbevolen', 'de aanbevolen dagelijkse hoeveelheid'];
    var WARN_ICON =
      '<svg class="juizs-imod-warn-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M12 3.5 2.5 20.5h19L12 3.5z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>' +
      '<path d="M12 10v4.5M12 17.5v.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

    var el = {
      index: root.querySelector('[data-juizs-imod-index]'),
      bottle: root.querySelector('[data-juizs-imod-bottle]'),
      eyebrow: root.querySelector('[data-juizs-imod-eyebrow]'),
      title: root.querySelector('[data-juizs-imod-title]'),
      portion: root.querySelector('[data-juizs-imod-portion]'),
      ings: root.querySelector('[data-juizs-imod-ings]'),
      ingsWrap: root.querySelector('[data-juizs-imod-ings-wrap]'),
      nutr: root.querySelector('[data-juizs-imod-nutr]'),
      nutrWrap: root.querySelector('[data-juizs-imod-nutr-wrap]'),
      pending: root.querySelector('[data-juizs-imod-pending]'),
      editor: root.querySelector('[data-juizs-imod-editor]'),
      allergens: root.querySelector('[data-juizs-imod-allergens]'),
      note: root.querySelector('[data-juizs-imod-note]'),
      prev: root.querySelector('[data-juizs-imod-prev]'),
      next: root.querySelector('[data-juizs-imod-next]'),
      meta: root.querySelector('[data-juizs-imod-meta]'),
    };

    var EMPTY_TEXT =
      root.getAttribute('data-empty-text') ||
      'Ingrediënten en voedingswaarden van dit sapje staan er binnenkort bij.';

    /* ---- Data-eilanden inlezen ---- */
    var sets = {};
    var setKeys = [];

    Array.prototype.forEach.call(document.querySelectorAll('[data-juizs-juices]'), function (node) {
      var key = node.getAttribute('data-juizs-juices');
      try {
        var parsed = JSON.parse(node.textContent);
        if (Array.isArray(parsed) && parsed.length) {
          sets[key] = parsed;
          setKeys.push(key);
        }
      } catch (err) {
        if (window.console) window.console.warn('[juizs-imodal] onleesbaar dagschema', key, err);
      }
    });

    if (!setKeys.length) return null;

    /* ---- Portal naar <body> ---- */
    var section = root.closest('.shopify-section');
    var sectionId = section && section.id ? section.id.replace(/^shopify-section-/, '') : null;

    Array.prototype.forEach.call(document.querySelectorAll('[data-juizs-imodal][data-juizs-portal]'), function (stale) {
      if (stale !== root && stale.parentNode) stale.parentNode.removeChild(stale);
    });
    if (root.parentNode !== document.body) document.body.appendChild(root);
    root.setAttribute('data-juizs-portal', '');

    var overlay = new window.JuizsOverlay(root);

    var activeKey = setKeys[0];
    var currentId = null;

    function juices() {
      return sets[activeKey] || [];
    }

    function find(id) {
      var list = juices();
      for (var i = 0; i < list.length; i++) {
        if (list[i].id === id) return list[i];
      }
      return null;
    }

    function indexOf(id) {
      var list = juices();
      for (var i = 0; i < list.length; i++) {
        if (list[i].id === id) return i;
      }
      return -1;
    }

    /* ---- Index links opbouwen ---- */
    // Elk onbekend type valt in "Extra" (achteraan), zodat een onverwachte
    // waarde nooit uit de index verdwijnt (29-07) en de groepen vast blijven.
    function typeOf(juice) {
      var type = String(juice.type || 'juice').trim().toLowerCase();
      return TYPE_ORDER.indexOf(type) === -1 ? 'extra' : type;
    }

    function labelFor(type) {
      return TYPE_LABEL[type];
    }

    function colorsOf(juice) {
      var fallback = TYPE_COLOR[typeOf(juice)];
      var light = HEX.test(juice.color) ? juice.color : null;
      var deep = HEX.test(juice.colorDeep) ? juice.colorDeep : light;
      return [light || fallback[0], deep || fallback[1]];
    }

    function gradient(juice) {
      var c = colorsOf(juice);
      return 'linear-gradient(180deg, ' + c[0] + ' 0%, ' + c[1] + ' 100%)';
    }

    function renderIndex() {
      if (!el.index) return;
      var list = juices();
      var html = '';

      el.index.setAttribute('aria-label', activeKey === 'all' ? 'Sapjes in dit pakket' : 'Sapjes in dit dagschema');

      TYPE_ORDER.forEach(function (type) {
        var group = list.filter(function (j) {
          return typeOf(j) === type;
        });
        if (!group.length) return;

        html +=
          '<details class="juizs-imod-group" open>' +
          '<summary class="juizs-imod-group-head">' +
          '<span class="juizs-imod-group-label">' +
          labelFor(type) +
          '</span>' +
          '<span class="juizs-imod-group-count">(' +
          group.length +
          ')</span>' +
          '</summary><ul class="juizs-imod-group-list">';

        group.forEach(function (j) {
          html +=
            '<li><button type="button" class="juizs-imod-item" data-imod-goto="' +
            escapeAttr(j.id) +
            '">' +
            '<span class="juizs-imod-dot" style="background:' +
            escapeAttr(gradient(j)) +
            '" aria-hidden="true"></span>' +
            '<span class="juizs-imod-item-name">' +
            escapeHtml(j.name) +
            '</span></button></li>';
        });

        html += '</ul></details>';
      });

      el.index.innerHTML = html;
    }

    function syncIndex() {
      if (!el.index) return;
      Array.prototype.forEach.call(el.index.querySelectorAll('[data-imod-goto]'), function (btn) {
        var on = btn.getAttribute('data-imod-goto') === currentId;
        btn.classList.toggle('is-current', on);
        if (on) {
          btn.setAttribute('aria-current', 'true');
        } else {
          btn.removeAttribute('aria-current');
        }
      });
    }

    /* ---- Hulpjes ---- */
    function escapeHtml(value) {
      return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function escapeAttr(value) {
      return escapeHtml(value);
    }

    function textEl(tag, text) {
      var node = document.createElement(tag);
      node.textContent = text;
      return node;
    }

    function rows(pairs, isPercentage) {
      return (pairs || [])
        .filter(function (pair) {
          return Array.isArray(pair) && pair.length >= 1;
        })
        .map(function (pair) {
          var name = pair[0];
          var value = pair.length > 1 ? pair[1] : '';
          if (isPercentage && typeof value === 'number') value = value + '%';
          return (
            '<li><span>' +
            escapeHtml(name) +
            '</span><span class="juizs-imod-v">' +
            escapeHtml(value) +
            '</span></li>'
          );
        })
        .join('');
    }

    /* Voedingswaarden: hoeveelheid (+ RI eronder), notitie of waarschuwing.
       Elke vorm uit het metaveld rendert iets; niets gooit een fout. */
    function cellText(cell) {
      return cell == null ? '' : String(cell).trim();
    }

    function isWarning(text) {
      var t = text.toLowerCase().replace(/\s+/g, ' ').replace(/^(hoog )caff?e[iï]ne/, '$1cafeïne');
      return WARN_PREFIXES.some(function (prefix) {
        return t.indexOf(prefix) === 0;
      });
    }

    function nutritionRows(list) {
      return (list || [])
        .map(function (row) {
          return Array.isArray(row) ? row : typeof row === 'string' ? [row] : null;
        })
        .filter(function (row) {
          return row && row.length >= 1;
        })
        .map(function (row) {
          var cells = row.map(cellText);
          if (cells.length > 1 && /\d/.test(cells[1])) {
            var extra = cells.slice(2).filter(Boolean).join(' · ');
            return (
              '<li' + (extra ? ' class="juizs-imod-has-extra"' : '') + '><span>' +
              escapeHtml(cells[0]) +
              '</span><span class="juizs-imod-v">' +
              escapeHtml(cells[1]) +
              '</span>' +
              (extra ? '<span class="juizs-imod-extra">' + escapeHtml(extra) + '</span>' : '') +
              '</li>'
            );
          }
          var text = cells.filter(Boolean).join(' · ');
          if (!text) return '';
          if (isWarning(text)) {
            return (
              '<li class="juizs-imod-line juizs-imod-warn">' + WARN_ICON +
              '<span><strong>Let op:</strong> ' + escapeHtml(text) + '</span></li>'
            );
          }
          return '<li class="juizs-imod-line">' + escapeHtml(text) + '</li>';
        })
        .join('');
    }

    function hasRows(pairs) {
      return Array.isArray(pairs) && pairs.length > 0;
    }

    /* ---- Vullen ---- */
    function populate(id) {
      var juice = find(id);
      if (!juice) return;
      currentId = id;

      var type = typeOf(juice);

      if (el.bottle) {
        el.bottle.style.background = gradient(juice);
        el.bottle.classList.toggle('is-short', type === 'shot' || type === 'veggie');
      }

      if (el.eyebrow) {
        var bits = [labelFor(type)];
        if (juice.note) bits.push(juice.note);
        if (juice.moment) bits.push('moment ' + juice.moment);
        el.eyebrow.textContent = bits.join(' · ');
        el.eyebrow.className = 'juizs-imod-eyebrow type-' + type;
      }

      if (el.title) el.title.textContent = juice.name;

      if (el.portion) {
        var meta = [];
        if (juice.time) meta.push(juice.time);
        if (juice.portion) meta.push(juice.portion);
        meta.push('koudgeperst');
        el.portion.textContent = meta.join(' · ');
      }

      var ings = hasRows(juice.ingredients);
      var nutr = hasRows(juice.nutrition);

      if (el.ings) el.ings.innerHTML = ings ? rows(juice.ingredients, true) : '';
      if (el.nutr) el.nutr.innerHTML = nutr ? nutritionRows(juice.nutrition) : '';
      if (el.ingsWrap) el.ingsWrap.hidden = !ings;
      if (el.nutrWrap) el.nutrWrap.hidden = !nutr;

      /* Leeg metaveld: sapje blijft staan, wachttekst in plaats van
         lege lijsten (afspraak Terry 29-07). */
      if (el.pending) {
        el.pending.hidden = ings || nutr;
        el.pending.textContent = EMPTY_TEXT;
      }

      /* Alleen in de theme-editor: waarom dit sapje leeg blijft. */
      if (el.editor) {
        el.editor.hidden = juice.linked !== false;
        el.editor.textContent = '';
        if (juice.linked === false) {
          el.editor.appendChild(textEl('strong', 'Editor-notitie (alleen zichtbaar in de theme-editor): '));
          el.editor.appendChild(document.createTextNode('Dagschema verwijst naar '));
          el.editor.appendChild(textEl('code', juice.id));
          el.editor.appendChild(document.createTextNode('; dat is geen product in de sappenset ('));
          el.editor.appendChild(textEl('code', 'juizs.max_juice_set'));
          el.editor.appendChild(document.createTextNode(').'));
        }
      }

      if (el.allergens) {
        el.allergens.innerHTML =
          '<strong>Allergenen:</strong> ' + escapeHtml(juice.allergens || 'Geen bekende allergenen');
      }

      if (el.note) {
        var showNote = !!juice.note && !!juice.time;
        el.note.hidden = !showNote;
        el.note.textContent = juice.note || '';
      }

      var idx = indexOf(id);
      var total = juices().length;
      if (el.prev) el.prev.disabled = idx <= 0;
      if (el.next) el.next.disabled = idx >= total - 1;
      if (el.meta) {
        // Zonder dagschema (eiland "all") is het geen dagschema maar het pakket.
        var schemaLabel = root.getAttribute('data-schema-label');
        var suffix = activeKey === 'all' ? '' : ' dagschema';
        el.meta.textContent =
          'Sap ' + (idx + 1) + ' van ' + total + (schemaLabel ? ' · ' + schemaLabel + suffix : '');
      }

      syncIndex();
    }

    function navigate(direction) {
      var idx = indexOf(currentId);
      var next = idx + direction;
      if (idx === -1 || next < 0 || next >= juices().length) return;
      populate(juices()[next].id);
    }

    function open(id, opener) {
      renderIndex();
      populate(id || juices()[0].id);
      overlay.open(opener);
    }

    /* ---- Bindingen ---- */
    on(document, 'click', function (e) {
      var trigger = e.target.closest('[data-juizs-juice]');
      if (trigger) {
        e.preventDefault();
        var variant = trigger.getAttribute('data-juizs-juice-variant');
        if (variant && sets[variant]) activeKey = variant;
        open(trigger.getAttribute('data-juizs-juice'), trigger);
        return;
      }

      var goto = e.target.closest('[data-imod-goto]');
      if (goto && root.contains(goto)) {
        e.preventDefault();
        populate(goto.getAttribute('data-imod-goto'));
      }
    });

    if (el.prev) {
      el.prev.addEventListener('click', function () {
        navigate(-1);
      });
    }
    if (el.next) {
      el.next.addEventListener('click', function () {
        navigate(1);
      });
    }

    on(document, 'keydown', function (e) {
      if (!overlay.isOpen) return;
      if (e.key === 'ArrowLeft') navigate(-1);
      else if (e.key === 'ArrowRight') navigate(1);
    });

    /* Buy-box wisselt van variant → ander dagschema, dus een andere set. */
    on(document, 'juizs:variant-change', function (e) {
      var id = e.detail && e.detail.variantId ? String(e.detail.variantId) : null;
      if (id && sets[id]) activeKey = id;
    });

    /* Opruimen: dicht (geeft de scroll-lock vrij), listeners los, weg uit <body>. */
    function destroy() {
      if (overlay.isOpen) overlay.close();
      listeners.forEach(function (l) {
        l[0].removeEventListener(l[1], l[2]);
      });
      listeners = [];
      if (root.parentNode) root.parentNode.removeChild(root);
    }

    return { sectionId: sectionId, destroy: destroy };
  }

  function start() {
    if (active) active.destroy();
    active = init();
  }

  window.JuizsImodal = { refresh: start };

  /* Theme-editor: de sectie wordt herladen of verwijderd. */
  document.addEventListener('shopify:section:unload', function (e) {
    if (!active || !e.detail || e.detail.sectionId !== active.sectionId) return;
    active.destroy();
    active = null;
  });
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-juizs-imodal]')) start();
  });

  // Vangnet: juizs-cart.js levert window.JuizsOverlay. Staat die (nog) niet
  // klaar — bijvoorbeeld omdat de scriptvolgorde ooit wijzigt — dan wachten we
  // op DOMContentLoaded; op dat moment zijn alle defer-scripts binnen.
  if (typeof window.JuizsOverlay === 'function') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
